import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import '../../components/public/Public.css';
import './Payment.css';

const METHODS = [
  { key:'momo_pay', icon:'📱', title:'Mobile Money', desc:'Pay with MTN, Vodafone, or AirtelTigo' },
  { key:'bank_pay', icon:'🏦', title:'Bank Transfer', desc:'Transfer to a generated bank account' },
  { key:'card_pay', icon:'💳', title:'Card Payment',  desc:'Pay with Visa or Mastercard' },
];

export default function PaymentPage() {
  const { transactionRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const meta = location.state || {};
  const [method, setMethod]   = useState('momo_pay');
  const [mobile, setMobile]   = useState(meta.phone || '');
  const [stage, setStage]     = useState('select'); // select | processing | bank | awaiting
  const [bankInfo, setBankInfo] = useState(null);
  const pollRef = useRef(null);

  /* ── Poll status ─────────────────────────────────────── */
  const checkStatus = useCallback(async () => {
    try {
      const r = await api.get(`/public/payment/status/${transactionRef}`);
      if (r.data.status === 'success') {
        clearInterval(pollRef.current);
        navigate(`/payment-success?ref=${transactionRef}`);
      } else if (['failed','timeout'].includes(r.data.status)) {
        clearInterval(pollRef.current);
        navigate(`/payment-failed?ref=${transactionRef}`);
      }
    } catch { /* keep polling */ }
  }, [transactionRef, navigate]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(checkStatus, 4000);
  }, [checkStatus]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  /* ── Initiate ────────────────────────────────────────── */
  const handlePay = async () => {
    const normalizedMobile = mobile.replace(/\s/g, '');
    if (method !== 'card_pay' && !/^\+233\d{9}$/.test(normalizedMobile)) {
      showToast('Enter a valid mobile number in the format +233248251913', 'warning');
      return;
    }
    setStage('processing');
    try {
      const r = await api.post('/public/payment/initiate', {
        transactionRef,
        mode_of_payment: method,
        mobile_num: mobile.replace(/\s/g,'') || '0000000000',
      });
      const data = r.data.data || {};

      if (method === 'card_pay') {
        if (!data.card_pay?.checkout_url) throw new Error('No checkout URL returned.');
        window.location.href = data.card_pay.checkout_url;
        return;
      }
      if (method === 'bank_pay') {
        console.log('Bank payment initiated:', data);
        if (!data.bank_pay?.bank_account_number) throw new Error('No bank account details returned.');
        setBankInfo(data.bank_pay);
        setStage('bank');
        startPolling();
        return;
      }
      // momo
      setStage('awaiting');
      startPolling();
    } catch (err) {
      showToast(err.response?.data?.error || err.message || 'Payment failed to start. Try again.', 'error');
      setStage('select');
    }
  };

  return (
    <div className="pub-page">
      <Navbar />
      <div className="pay-wrap">
        <div className="pay-container">

          {/* Order summary */}
          <div className="pay-summary">
            <h3>Order Summary</h3>
            {meta.eventTitle && <div className="pay-summary-event">{meta.eventTitle}</div>}
            <div className="pay-summary-row">
              <span>Reference</span>
              <code>{transactionRef}</code>
            </div>
            <div className="pay-summary-total">
              <span>Total</span>
              <strong>{meta.currency || ''} {parseFloat(meta.amount || 0).toFixed(2)}</strong>
            </div>
          </div>

          {/* Main panel */}
          <div className="pay-panel">

            {stage === 'select' && (
              <>
                <h2>Choose a payment method</h2>
                <p className="pay-sub">Select how you'd like to pay for your registration.</p>

                <div className="pay-methods">
                  {METHODS.map(m => (
                    <label key={m.key} className={`pay-method ${method === m.key ? 'active' : ''}`}>
                      <input type="radio" name="method" value={m.key} checked={method === m.key} onChange={() => setMethod(m.key)} />
                      <span className="pay-method-icon">{m.icon}</span>
                      <span className="pay-method-text">
                        <strong>{m.title}</strong>
                        <small>{m.desc}</small>
                      </span>
                    </label>
                  ))}
                </div>

                {method !== 'card_pay' && (
                  <div className="form-group" style={{marginTop:'1.5rem'}}>
                    <label className="form-label">
                      {method === 'momo_pay' ? 'Mobile Money Number' : 'Mobile Number'}
                    </label>
                    <input
                      className="form-control form-control-lg"
                      value={mobile}
                      onChange={e => setMobile(e.target.value)}
                      placeholder="e.g. 233241234567"
                      inputMode="numeric"
                    />
                    {method === 'momo_pay' && (
                      <small className="form-hint">You'll receive a prompt on this number to authorize payment.</small>
                    )}
                  </div>
                )}

                <button className="pub-btn pub-btn-primary pay-btn" onClick={handlePay}>
                  Pay {meta.currency || ''} {parseFloat(meta.amount || 0).toFixed(2)} →
                </button>
              </>
            )}

            {stage === 'processing' && (
              <div className="pay-status">
                <div className="spinner" />
                <h2>Initiating payment…</h2>
                <p className="pay-sub">Please wait a moment.</p>
              </div>
            )}

            {stage === 'awaiting' && (
              <div className="pay-status">
                <div className="pay-pulse">📱</div>
                <h2>Check your phone</h2>
                <p className="pay-sub">
                  A payment prompt has been sent to <strong>{mobile}</strong>.
                  Enter your Mobile Money PIN to approve. This page will update automatically once payment is confirmed.
                </p>
                <div className="pay-awaiting-indicator">
                  <span className="spinner spinner-sm" /> Waiting for confirmation…
                </div>
                <div className="pay-momo-tip">
                  <strong>Didn't get the prompt?</strong> On MTN, open your MoMo app or dial <strong>*170#</strong>,
                  go to <strong>My Approvals</strong>, and approve the pending transaction from there.
                </div>
                <button className="reg-link-btn" onClick={() => navigate(`/payment-failed?ref=${transactionRef}`)}>
                  Cancel payment
                </button>
              </div>
            )}

            {stage === 'bank' && (
              <div className="pay-status">
                <h2>Complete your bank transfer</h2>
                <p className="pay-sub">Transfer the exact amount to the account below. We'll confirm automatically.</p>
                <div className="pay-bank-details">
                  <div className="pay-bank-row">
                    <span>Bank Name</span>
                    <strong>{bankInfo?.bank_name || '—'}</strong>
                  </div>
                  <div className="pay-bank-row">
                    <span>Account Number</span>
                    <strong className="pay-account">{bankInfo?.bank_account_number || '—'}</strong>
                  </div>
                  <div className="pay-bank-row">
                    <span>Amount</span>
                    <strong>{meta.currency || ''} {parseFloat(meta.amount || 0).toFixed(2)}</strong>
                  </div>
                  <div className="pay-bank-row">
                    <span>Reference</span>
                    <strong>{transactionRef}</strong>
                  </div>
                </div>
                <div className="pay-awaiting-indicator">
                  <span className="spinner spinner-sm" /> Waiting for transfer confirmation…
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
